/**
 * Music stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stickers`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const musicStickersPack: ToilePack = {
    slug: 'music-stickers',
    category: 'sticker',
    name: 'Music stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'cassette', label: 'Cassette', subject: 'a cassette tape with a blank label'},
        {key: 'headphones', label: 'Headphones', subject: 'a pair of over-ear headphones'},
        {key: 'vinyl', label: 'Vinyl record', subject: 'a vinyl record with a plain centre label'},
    ],
    styles: [
        {key: 'retro', label: 'Retro', style: 'retro sticker illustration, 1970s shapes, halftone grain, slight misregistration', palette: 'Mustard, rust, teal and cream', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'bold', label: 'Bold cartoon', style: 'bold cartoon sticker, thick black outlines, bright flat fills, slight gloss', palette: 'Bright saturated primaries with black outlines', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'minimal', label: 'Minimal line', style: 'minimal single-line sticker, one clean continuous line, no fill', palette: 'Black line only, one accent colour', framing: 'Each sticker isolated with a thin white die-cut border on a plain ground'},
    ],
    directions: ['a guitar instead', 'for a laptop lid', 'in neon colors'],
    directionHint: 'try: a guitar instead',
    seo: {
        title: 'Music Stickers: Cassette, Headphones, Vinyl',
        metaDescription: 'Nine music stickers — a cassette tape, headphones, a vinyl record — in retro, bold cartoon, and minimal line styles.',
        h1: 'Music stickers: cassette tape, headphones, vinyl record',
        tagline: 'Nine stickers, three music icons, three styles each.',
        intro: 'A cassette tape with a blank label, a pair of over-ear headphones, and a vinyl record with a plain centre label — three music stickers, die-cut and ready to print. Each rendered retro, bold cartoon, and minimal single-line. Ask for a guitar instead, a boombox scene, or a concert composition, and the same three styles carry it.',
        useCases: [
            'a laptop or water bottle',
            'a planner or journal page',
            'a phone case sticker sheet',
            'a gift for a music lover',
        ],
        faq: [
            {q: "What's in this pack?", a: 'Nine music stickers — a cassette tape, headphones, and a vinyl record — in retro, bold cartoon, and minimal line finishes.'},
            {q: 'Can I get a guitar or a different music item?', a: 'Yes — the direction field takes any variation, including a guitar instead, a boombox scene, or a concert composition.'},
            {q: 'What size should I print these at?', a: 'Each sticker is a square die-cut design, so it scales cleanly from a small vinyl sticker to a full A4 sheet.'},
            {q: 'Do the cassette and record carry real labels or artist names?', a: 'No — both ship with a blank label by design, so nothing infringes on a real release.'},
        ],
    },
}
